import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import PackagesPopup from "../popups/PackagesPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import {
  apiDeletePackage,
  apiGetAllPackages,
  apiGetAllPackagesByProduct,
  apiGetAllPackagesByProductGroup,
} from "../services/PackagesService";

interface Props {
  contextLevel: string;
}

const entityName = 'package';
const translationPrefix = 'Packages';
const translationModel = { prefix: translationPrefix };

const PackagesPage = (props: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
  const [loading, setLoading] = useState(false);
  const [productInfoSelected, setProductInfoSelected] = useState(false);
  const [data, setData] = useState({ rows: [] });
  const [packagePopupData, setPackagePopupData] = useState({
    visible: false,
    id: undefined,
  });

  const selectedProductInfo = useSelector((state: any) => state.productInfo);
  useEffect(() => {
    loadData();
  }, [selectedProductInfo]);

  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const userInfo = useSelector((state: any) => state.userInfo);

  const setRoles = () => {
    setCanCreate(checkCrud(entityName, 'create', userInfo));
    setCanRead(checkCrud(entityName, 'read', userInfo));
    setCanUpdate(checkCrud(entityName, 'update', userInfo));
    setCanDelete(checkCrud(entityName, 'delete', userInfo));
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])

  const loadData = () => {
    if (selectedProductInfo) {
      if (selectedProductInfo.productId) {
        setLoading(true);
        setProductInfoSelected(true);
        apiGetAllPackagesByProduct(selectedProductInfo.productId).then(
          (responseData) => {
            setData({
              rows: responseData.data,
            });

            setLoading(false);
          }
        );
      } else if (selectedProductInfo.group) {
        setLoading(true);
        setProductInfoSelected(true);
        apiGetAllPackagesByProductGroup(selectedProductInfo.group.id).then(
          (responseData) => {
            setData({
              rows: responseData.data,
            });

            setLoading(false);
          }
        );
      }
    }
  };

  const handleDeleteRow = (row) => {
    apiDeletePackage(row.id)
      .then((responseData) => {
        dispatch(
          addNotification({
            text: FormatString(t('Popup.DeleteSuccess'), row.code),
            type: "success",
          })
        );
        loadData();

        setLoading(false);
      })
      .catch((err: any) => {
        dispatch(
          addNotification({
            text: FormatString(t('Popup.DeleteFailure'), row.code),
            type: "error",
          })
        );
      });
  };

  return (
    <TranslationProvider value={translationModel}>
      {packagePopupData.visible && (
        <PackagesPopup
          id={packagePopupData.id}
          refreshData={() => loadData()}
          handleCancel={() => {
            setPackagePopupData({ visible: false, id: undefined });
          }}
        />
      )}
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel="Balení"
        rightAreaComponent={
          <AddButtonInTopBar
            setPopupData={setPackagePopupData}
            text={t('CreateText')}
            enabled={canCreate}
          />
        }
      />

      <PageContent>
        <>
          <ProductBreadCrumb selectionOfProductIsRequired={true} />
          {productInfoSelected && (
            <DataGrid
              loading={loading}
              rows={data.rows}
              showDeleteButton={canDelete}
              showEditButton={canUpdate}
              onDeleteRow={handleDeleteRow}
              handleEditAction={(id: any) => {
                setPackagePopupData({ visible: true, id: id });
              }}
              cols={[
                {
                  name: t('code'),
                  dataField: "code",
                  sortable: true,
                  filterable: true,
                  filterType: "text",
                },
                {
                  name: t('productName'),
                  dataField: "productName",
                  isLinkToItem: true,
                  sortable: true,
                  filterable: true,
                  filterType: "text",
                },
                {
                  name: t('basisName'),
                  dataField: "basisName",
                  isLinkToItem: false,
                  sortable: true,
                  filterable: true,
                  filterType: "text",
                },

                {
                  name: t('formattedAmmountInKg'),
                  dataField: "formattedAmmountInKg",
                  sortable: true,
                  filterable: true,
                  fieldType: "numeric"
                },
                {
                  name: t('formattedAmmountInLitres'),
                  dataField: "formattedAmmountInLitres",
                  sortable: true,
                  filterable: true,
                  fieldType: "numeric"
                },
                {
                  name: t('basisDensity'),
                  dataField: "basisDensity",
                  sortable: true,
                  filterable: true,
                  fieldType: "numeric"
                },
                {
                  name: t('price'),
                  dataField: "price",
                  sortable: true,
                  filterable: true,
                  fieldType: "numeric"
                },
                {
                  name: t('hardeners'),
                  dataField: "hardeners",
                  sortable: false,
                  filterable: false,
                  fieldType: "multi",
                  displayField: "nameAndSize"
                },
                { name: t('noteInternal'), dataField: "noteInternal" },
                {
                  name: t('createdOn'),
                  fieldType: "datetime",
                  dataField: "createdOn",
                  sortable: true,
                },
                {
                  name: t('modifiedOn'),
                  fieldType: "datetime",
                  dataField: "modifiedOn",
                  sortable: true,
                },
              ]}
            />
          )}
        </>
      </PageContent>
    </TranslationProvider>
  );
};

export default PackagesPage;
